import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, ShoppingBag, Heart, Shield, Moon, Loader2, Volume2, VolumeX } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useProducts } from '../context/ProductContext';

// Welcome video with an unmute toggle overlay (autoplay requires muted=true)
const WelcomeVideo = () => {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    // Flip mute state immediately — single tap is enough, no seek/restart needed
    const wasMuted = video.muted;
    video.muted = !wasMuted;
    setMuted(!wasMuted);
    // Make sure the video is playing (autoplay may have been paused by the browser)
    if (video.paused) {
      const p = video.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    }
  };

  return (
    <div className="relative">
      <video
        ref={(el) => {
          videoRef.current = el;
          if (el) {
            el.muted = true;
            el.defaultMuted = true;
          }
        }}
        src="/welcome.mp4"
        poster="https://customer-assets.emergentagent.com/job_1984c3e1-4b2c-4786-b54b-12533e8a13f2/artifacts/jmi3v5nh_PSX_20260420_113201.jpg"
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        className="w-full h-auto object-cover block"
        data-testid="home-daizy-video"
      >
        <img
          src="https://customer-assets.emergentagent.com/job_1984c3e1-4b2c-4786-b54b-12533e8a13f2/artifacts/jmi3v5nh_PSX_20260420_113201.jpg"
          alt="Daizy, founder of Infused by Daizy"
          className="w-full h-auto object-cover"
        />
      </video>

      {/* Tap-to-unmute button (bottom-right of video) */}
      <button
        onClick={toggleMute}
        aria-label={muted ? 'Unmute welcome video' : 'Mute welcome video'}
        className="absolute bottom-4 right-4 flex items-center gap-2 px-3.5 py-2 rounded-full bg-black/60 hover:bg-black/80 backdrop-blur-md border border-yellow-400/50 text-yellow-300 hover:text-yellow-200 transition-all shadow-lg"
        data-testid="welcome-unmute-btn"
      >
        {muted ? (
          <>
            <VolumeX className="w-4 h-4" />
            <span className="text-xs font-medium tracking-wide">Tap for sound</span>
          </>
        ) : (
          <Volume2 className="w-4 h-4" />
        )}
      </button>
    </div>
  );
};

// Animated Moon Component
const AnimatedMoon = () => (
  <div className="absolute top-20 right-10 md:right-20 lg:right-32 pointer-events-none">
    <div className="relative">
      {/* Moon glow effect */}
      <div className="absolute inset-0 blur-3xl bg-yellow-200/30 rounded-full scale-150 animate-pulse" />
      {/* Main moon */}
      <div className="relative w-24 h-24 md:w-32 md:h-32 lg:w-40 lg:h-40">
        <svg viewBox="0 0 100 100" className="w-full h-full drop-shadow-2xl">
          {/* Moon gradient */}
          <defs>
            <radialGradient id="moonGradient" cx="30%" cy="30%">
              <stop offset="0%" stopColor="#FFF9E6" />
              <stop offset="50%" stopColor="#FFE98A" />
              <stop offset="100%" stopColor="#E6C547" />
            </radialGradient>
            <filter id="moonGlow">
              <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
              <feMerge>
                <feMergeNode in="coloredBlur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
          </defs>
          {/* Full moon circle */}
          <circle cx="50" cy="50" r="45" fill="url(#moonGradient)" filter="url(#moonGlow)" />
          {/* Moon craters/texture */}
          <circle cx="35" cy="35" r="8" fill="#D4B83D" opacity="0.3" />
          <circle cx="60" cy="45" r="5" fill="#D4B83D" opacity="0.25" />
          <circle cx="45" cy="65" r="6" fill="#D4B83D" opacity="0.2" />
          <circle cx="70" cy="60" r="4" fill="#D4B83D" opacity="0.15" />
          <circle cx="30" cy="55" r="3" fill="#D4B83D" opacity="0.2" />
        </svg>
      </div>
      {/* Subtle sparkles around moon */}
      <div className="absolute -top-2 -right-2 text-yellow-300 animate-pulse" style={{ animationDelay: '0.5s' }}>
        <Sparkles className="w-4 h-4" />
      </div>
      <div className="absolute -bottom-1 -left-3 text-yellow-200 animate-pulse" style={{ animationDelay: '1s' }}>
        <Sparkles className="w-3 h-3" />
      </div>
    </div>
  </div>
);

const Home = () => {
  const { products, loading } = useProducts();
  const featuredProducts = products.slice(0, 2);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1623172959921-630212f71058)',
            filter: 'brightness(0.4)'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-purple-900/80 via-purple-800/70 to-purple-950/90" />
        
        {/* Animated Moon */}
        <AnimatedMoon />
        
        <div className="relative z-10 container mx-auto px-4 text-center text-white">
          <div className="flex justify-center mb-6">
            <Sparkles className="w-16 h-16 text-yellow-400 animate-pulse" />
          </div>
          <h1 className="text-5xl md:text-7xl font-bold mb-6 tracking-tight">
            Infused by <span className="text-white">Daizy</span>
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto text-purple-100">
            Magically Charged Ritual Oils & Intention Blends
          </p>
          <p className="text-lg mb-6 max-w-2xl mx-auto text-purple-200">
            Hand-blended with sacred oils, anointed herbs, and crystal energy
          </p>
          <p className="text-base md:text-lg mb-10 max-w-3xl mx-auto text-yellow-200 italic">
            ✦ Perfect for ritual oils, glamour magic, ceremonies, spiritual growth & healing ✦
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/products">
              <Button size="lg" className="bg-yellow-500 hover:bg-yellow-600 text-purple-900 font-semibold px-8 py-6 text-lg">
                <ShoppingBag className="mr-2" />
                Shop Ritual Oils
              </Button>
            </Link>
            <Link to="/about">
              <Button size="lg" variant="outline" className="border-2 border-yellow-400 text-yellow-400 hover:bg-yellow-400 hover:text-purple-900 px-8 py-6 text-lg">
                Our Story
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gradient-to-b from-purple-950 to-purple-900 relative">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="bg-purple-800/50 border-purple-600 backdrop-blur-sm">
              <CardContent className="pt-6 text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-yellow-500/20 rounded-full flex items-center justify-center">
                  <Sparkles className="w-8 h-8 text-yellow-400" />
                </div>
                <h3 className="text-xl font-semibold mb-2 text-white">Moon Phase Charged</h3>
                <p className="text-purple-200">Each oil is charged with intention during appropriate moon phases for maximum potency.</p>
              </CardContent>
            </Card>
            
            <Card className="bg-purple-800/50 border-purple-600 backdrop-blur-sm">
              <CardContent className="pt-6 text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-yellow-500/20 rounded-full flex items-center justify-center">
                  <Heart className="w-8 h-8 text-yellow-400" />
                </div>
                <h3 className="text-xl font-semibold mb-2 text-white">Hand-Blended with Love</h3>
                <p className="text-purple-200">Premium natural absolutes and essential oils carefully crafted with intention.</p>
              </CardContent>
            </Card>
            
            <Card className="bg-purple-800/50 border-purple-600 backdrop-blur-sm">
              <CardContent className="pt-6 text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-yellow-500/20 rounded-full flex items-center justify-center">
                  <Shield className="w-8 h-8 text-yellow-400" />
                </div>
                <h3 className="text-xl font-semibold mb-2 text-white">Sacred Ingredients</h3>
                <p className="text-purple-200">Anointed herbs, flowers, heartwoods, resins, and ethically sourced energy crystals.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Welcome from Daizy */}
      <section className="py-20 bg-gradient-to-b from-purple-900 to-purple-950 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-64 h-64 bg-yellow-500 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-purple-500 rounded-full blur-3xl" />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            {/* Daizy's Welcome Video (Ken Burns + animated message + healing tones) */}
            <div className="relative order-2 lg:order-1">
              <div className="absolute -inset-4 bg-gradient-to-br from-yellow-400/40 via-purple-500/30 to-yellow-400/40 rounded-3xl blur-2xl" />
              <div className="relative rounded-3xl overflow-hidden border-4 border-yellow-400/60 shadow-2xl shadow-yellow-500/30">
                <WelcomeVideo />
              </div>
              {/* Decorative sparkles */}
              <div className="absolute -top-4 -right-4 text-yellow-400">
                <Sparkles className="w-10 h-10 animate-pulse" />
              </div>
              <div className="absolute -bottom-4 -left-4 text-yellow-300" style={{ animationDelay: '0.5s' }}>
                <Moon className="w-8 h-8 animate-pulse" />
              </div>
            </div>

            {/* Welcome Message */}
            <div className="order-1 lg:order-2 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-yellow-500/15 border border-yellow-400/40 mb-6">
                <Sparkles className="w-4 h-4 text-yellow-400" />
                <span className="text-yellow-300 text-sm tracking-widest uppercase">A Message from Daizy</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
                Welcome, Beautiful Soul
              </h2>
              <p className="text-xl text-yellow-200 italic mb-6 leading-relaxed">
                Your spirit is sacred. Your healing is sacred. You are sacred.
              </p>
              <p className="text-purple-100 text-base md:text-lg mb-6 leading-relaxed">
                Every oil is hand-blended with intention, anointed with sacred herbs, and
                charged beneath the moon to carry you deeper into your spiritual journey.
                Whether you seek <span className="text-yellow-300 font-semibold">healing</span>,{' '}
                <span className="text-yellow-300 font-semibold">protection</span>,{' '}
                <span className="text-yellow-300 font-semibold">love</span>, or{' '}
                <span className="text-yellow-300 font-semibold">inner peace</span> — may these blends
                awaken the divine energy that already lives within you.
              </p>
              <p className="text-purple-200 text-base md:text-lg leading-relaxed">
                From the bottom of my heart — <span className="text-yellow-300 font-semibold">thank you</span>{' '}
                for taking the time to explore what I have to offer. Your presence here is{' '}
                <span className="text-yellow-300 font-semibold">alignment</span>, and I hope something
                in this shop reaches the part of your spirit that&apos;s been calling for it. ✦
              </p>
              <p className="mt-6 text-2xl font-bold text-yellow-400" style={{ fontFamily: 'cursive' }}>
                — Daizy
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-20 bg-gradient-to-b from-purple-900 to-purple-950 relative">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Featured Ritual Oils</h2>
            <p className="text-xl text-purple-200">Discover our magically charged collection</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {featuredProducts.map(product => (
              <Link key={product.id} to={`/product/${product.id}`}>
                <Card className="bg-purple-800/40 border-purple-600 hover:border-yellow-500 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-yellow-500/20 overflow-hidden group">
                  <div className="relative h-64 overflow-hidden">
                    <img 
                      src={product.image} 
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                    <div className="absolute top-4 right-4 bg-yellow-500 text-purple-900 px-3 py-1 rounded-full text-sm font-semibold">
                      ${product.basePrice ? product.basePrice.toFixed(2) : product.price?.toFixed(2)}
                    </div>
                  </div>
                  <CardContent className="pt-6">
                    <p className="text-purple-300 text-sm mb-2">{product.category}</p>
                    <h3 className="text-xl font-semibold mb-2 text-white">{product.name}</h3>
                    <p className="text-purple-200 text-sm line-clamp-2">{product.description}</p>
                    {product.uses && product.uses.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        {product.uses.slice(0, 3).map((use) => (
                          <span
                            key={`home-use-${product.id}-${use}`}
                            className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-yellow-500/15 text-yellow-300 border border-yellow-500/30"
                          >
                            {use}
                          </span>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link to="/products">
              <Button size="lg" className="bg-yellow-500 hover:bg-yellow-600 text-purple-900 font-semibold px-8">
                View All Products
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-b from-purple-900 to-purple-950 relative">
        <div className="container mx-auto px-4 text-center">
          <Sparkles className="w-16 h-16 mx-auto mb-6 text-yellow-400 animate-pulse" />
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Transform Your Spiritual Practice?
          </h2>
          <p className="text-xl text-purple-200 mb-8 max-w-2xl mx-auto">
            Explore our collection of intention oils and ritual blends
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-yellow-500 hover:bg-yellow-600 text-purple-900 font-semibold px-8 py-6 text-lg">
              Get in Touch
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
